<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">Beziehungen</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('relationships')"
                                :key="badge.key"
                                :title="badge.title || 'Shortcut'"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        id="filter-button"
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="toggleFiltersSidebar"
                    >
                        <span
                            v-if="hasActiveFilters"
                            class="stage-filter-active-dot"
                            aria-hidden="true"
                        ></span>
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="relationships-surface w-full h-full bg-white shadow rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>
                <div
                    :class="{'relationships-layout-empty': !loading && relationships.length === 0}"
                    class="relationships-layout flex-1 min-h-0"
                >
                    <section class="relationships-list flex flex-col min-h-0" aria-label="Beziehungsliste">
                        <relationships-hub-toolbar
                            active-section="contacts"
                            :show-actions="canCreateExternalContacts"
                            :show-view-switch="relationships.length > 0"
                            :view-mode="viewMode"
                            @update:viewMode="setViewMode"
                        >
                            <template v-slot:actions>
                                <button
                                    aria-label="Externen Kontakt anlegen"
                                    class="btn-stage-icon-plain"
                                    title="Externen Kontakt anlegen"
                                    type="button"
                                    @click="openExternalContactDialog"
                                >
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                                </button>
                            </template>
                        </relationships-hub-toolbar>
                        <div
                            :class="{'relationships-list-grid': viewMode === 'grid' && relationships.length > 0}"
                            class="relationships-list-body flex-1 min-h-0 overflow-auto"
                        >
                            <div
                                v-for="relationship in paginatedRelationships"
                                :key="relationship.id"
                                :class="{
                                    'relationships-row-selected': relationship.id === selectedRelationshipId,
                                    'relationships-row-admin': isAdmin,
                                    'relationships-row-card': viewMode === 'grid',
                                    'relationships-row-list-actions': viewMode !== 'grid',
                                }"
                                class="relationships-row"
                                tabindex="0"
                                @click="selectRelationship(relationship.id)"
                                @keydown.enter.prevent="selectRelationship(relationship.id)"
                                @keydown.space.prevent="selectRelationship(relationship.id)"
                            >
                                <div class="relationships-person">
                                    <span
                                        v-if="!relationship.avatar"
                                        :style="avatarStyle(relationship)"
                                        class="relationships-avatar"
                                        aria-hidden="true"
                                    >
                                        {{ relationship.initials }}
                                    </span>
                                    <img
                                        v-else
                                        :alt="relationship.name"
                                        :src="relationship.avatar"
                                        class="relationships-avatar object-cover"
                                    >
                                    <span class="min-w-0">
                                        <span class="relationships-person-title">
                                            <span :title="relationship.name" class="truncate">{{ relationship.name }}</span>
                                            <span
                                                v-if="relationshipBadgeLabel(relationship)"
                                                :title="relationship.hasAccount ? 'Systemrolle' : 'Kontakt ohne Systemaccount'"
                                                class="relationships-account-badge"
                                            >
                                                {{ relationshipBadgeLabel(relationship) }}
                                            </span>
                                        </span>
                                        <span :title="relationshipSubtitle(relationship)" class="block mt-1 text-xs text-gray-500 truncate">
                                            {{ relationshipSubtitle(relationship) }}
                                        </span>
                                    </span>
                                </div>

                                <div
                                    v-if="viewMode === 'grid' && (relationship.email || relationship.phone || relationship.mobile || addressText(relationship))"
                                    class="relationships-card-details"
                                >
                                    <div v-if="relationship.email" class="relationships-card-line">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.envelope"></svg-vue>
                                        <a
                                            :href="`mailto:${relationship.email}`"
                                            :title="relationship.email"
                                            class="relationships-card-link min-w-0 truncate"
                                            @click.stop
                                        >
                                            {{ relationship.email }}
                                        </a>
                                    </div>
                                    <div v-if="relationship.phone || relationship.mobile" class="relationships-card-line">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.phone"></svg-vue>
                                        <a
                                            :href="phoneHref(relationshipCardPhone(relationship))"
                                            :title="relationshipCardPhone(relationship)"
                                            class="relationships-card-link min-w-0 truncate"
                                            @click.stop
                                        >
                                            {{ relationshipCardPhone(relationship) }}
                                        </a>
                                    </div>
                                    <div v-if="addressText(relationship)" class="relationships-card-line">
                                        <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.location-dot"></svg-vue>
                                        <a
                                            :href="addressHref(relationship)"
                                            :title="addressText(relationship)"
                                            class="relationships-card-link min-w-0 truncate"
                                            rel="noopener noreferrer"
                                            target="_blank"
                                            @click.stop
                                        >
                                            {{ addressText(relationship) }}
                                        </a>
                                    </div>
                                </div>

                                <div v-if="viewMode !== 'grid'" class="relationships-count">
                                    <template v-if="relationship.hasAccount">
                                        <span>{{ totalConnections(relationship) }} Verbindungen</span>
                                    </template>
                                    <span v-else>–</span>
                                </div>
                                <div
                                    v-if="isAdmin || (viewMode !== 'grid' && (relationship.email || relationshipCardPhone(relationship)))"
                                    class="relationships-actions"
                                    @click.stop
                                >
                                    <div class="relative inline-flex items-center justify-center">
                                        <button
                                            :aria-expanded="activeActionsRelationshipId === relationship.id ? 'true' : 'false'"
                                            aria-haspopup="menu"
                                            aria-label="Aktionen"
                                            class="inline-flex items-center justify-center h-7 w-7 text-gray-500"
                                            title="Aktionen"
                                            type="button"
                                            @click.stop.prevent="toggleRowActions(relationship.id)"
                                        >
                                            <span class="flex flex-col items-center justify-center space-y-0.5">
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                                <span class="block rounded-full bg-current" style="width:3px;height:3px;"></span>
                                            </span>
                                        </button>
                                        <div
                                            v-if="activeActionsRelationshipId === relationship.id"
                                            class="relationships-actions-menu absolute right-0 top-full z-30 mt-2 overflow-hidden rounded border border-bordergray-100 bg-white shadow-sm"
                                            role="menu"
                                        >
                                            <template v-if="isAdmin">
                                                <button
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                    type="button"
                                                    @click.stop.prevent="editRelationship(relationship)"
                                                >
                                                    {{ relationship.hasAccount ? 'Account bearbeiten' : 'Kontakt bearbeiten' }}
                                                </button>
                                                <button
                                                    v-if="!relationship.hasAccount"
                                                    class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                    role="menuitem"
                                                    type="button"
                                                    @click.stop.prevent="openExternalContactDeleteDialog(relationship)"
                                                >
                                                    Löschen
                                                </button>
                                            </template>
                                            <a
                                                v-if="viewMode !== 'grid' && relationship.email"
                                                :href="`mailto:${relationship.email}`"
                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                                @click="closeRowActions"
                                            >
                                                E-Mail senden
                                            </a>
                                            <a
                                                v-if="viewMode !== 'grid' && relationshipCardPhone(relationship)"
                                                :href="phoneHref(relationshipCardPhone(relationship))"
                                                class="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                role="menuitem"
                                                @click="closeRowActions"
                                            >
                                                Anrufen
                                            </a>
                                        </div>
                                    </div>
                                </div>
                                <div v-else-if="viewMode !== 'grid'" aria-hidden="true"></div>
                                <svg-vue
                                    class="relationships-row-arrow h-4 w-4 text-gray-300"
                                    icon="font-awesome.regular.chevron-right"
                                ></svg-vue>
                            </div>

                            <div v-if="!loading && relationships.length === 0" class="stage-empty-state">
                                <div class="stage-empty-state-inner">
                                    <span class="stage-empty-state-icon-wrap">
                                        <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                                    </span>
                                    <h3 class="stage-empty-state-title">Keine Beziehungen gefunden</h3>
                                </div>
                            </div>
                        </div>

                        <list-footer-pagination
                            :start="paginationStart"
                            :end="paginationEnd"
                            :total="pagination.total"
                            :current-page="pagination.currentPage"
                            :total-pages="pagination.totalPages"
                            nav-class="relationships-pagination"
                            @prev="changePage(currentPage - 1)"
                            @next="changePage(currentPage + 1)"
                        />
                    </section>

                    <div
                        v-if="selectedRelationship && mobileDetailOpen"
                        class="relationships-detail-backdrop"
                        aria-hidden="true"
                        @click="closeMobileDetail"
                    ></div>

                    <section
                        v-if="selectedRelationship"
                        :class="{'relationships-detail-mobile-open': mobileDetailOpen}"
                        class="relationships-detail min-h-0"
                        aria-labelledby="relationship-detail-title"
                    >
                        <div class="relationships-detail-body">
                        <div class="relationships-detail-toolbar">
                            <button
                                class="relationships-detail-close"
                                title="Detailansicht schließen"
                                aria-label="Detailansicht schließen"
                                type="button"
                                @click="closeMobileDetail"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.chevron-right"></svg-vue>
                            </button>
                            <span class="relationships-detail-toolbar-title">Kontaktdetails</span>
                        </div>
                        <div class="relationships-detail-header">
                            <span
                                v-if="!selectedRelationship.avatar"
                                :style="avatarStyle(selectedRelationship)"
                                class="relationships-avatar relationships-avatar-large"
                                aria-hidden="true"
                            >
                                {{ selectedRelationship.initials }}
                            </span>
                            <img
                                v-else
                                :alt="selectedRelationship.name"
                                :src="selectedRelationship.avatar"
                                class="relationships-avatar relationships-avatar-large object-cover"
                            >
                            <div class="min-w-0 flex-1">
                                <div class="flex flex-wrap items-center gap-2">
                                    <h2 id="relationship-detail-title" class="text-xl font-semibold leading-tight text-gray-900">
                                        {{ selectedRelationship.name }}
                                    </h2>
                                    <span
                                        v-if="relationshipBadgeLabel(selectedRelationship)"
                                        :title="selectedRelationship.hasAccount ? 'Systemrolle' : 'Kontakt ohne Systemaccount'"
                                        class="relationships-account-badge"
                                    >
                                        {{ relationshipBadgeLabel(selectedRelationship) }}
                                    </span>
                                </div>
                                <p class="mt-0.5 text-sm leading-4 text-gray-500">
                                    {{ relationshipSubtitle(selectedRelationship) }}
                                </p>
                            </div>
                        </div>

                        <div class="relationships-main-section-header relationships-contact-section-header">
                            <h3 class="relationships-main-section-title">Kontakt</h3>
                        </div>

                        <div
                            v-if="hasSelectedRelationshipContactData"
                            id="relationship-contact-details"
                            class="relationships-contact-details"
                        >
                            <table class="relationships-contact-table">
                                <tbody>
                                    <tr v-if="selectedRelationship.email">
                                        <th scope="row">E-Mail</th>
                                        <td>
                                            <a
                                                :href="`mailto:${selectedRelationship.email}`"
                                                class="relationships-contact-link"
                                            >
                                                {{ selectedRelationship.email }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedRelationship.phone">
                                        <th scope="row">Telefon</th>
                                        <td>
                                            <a
                                                :href="phoneHref(selectedRelationship.phone)"
                                                class="relationships-contact-link"
                                            >
                                                {{ selectedRelationship.phone }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedRelationship.mobile">
                                        <th scope="row">Mobil</th>
                                        <td>
                                            <a
                                                :href="phoneHref(selectedRelationship.mobile)"
                                                class="relationships-contact-link"
                                            >
                                                {{ selectedRelationship.mobile }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedRelationship.website">
                                        <th scope="row">Website</th>
                                        <td>
                                            <a
                                                :href="websiteHref(selectedRelationship.website)"
                                                class="relationships-contact-link"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                {{ selectedRelationship.website }}
                                            </a>
                                        </td>
                                    </tr>
                                    <tr v-if="selectedRelationshipSocialLinks.length > 0" class="relationships-social-row">
                                        <th scope="row">Social Media</th>
                                        <td>
                                            <span class="relationships-social-links">
                                                <a
                                                    v-for="socialLink in selectedRelationshipSocialLinks"
                                                    :key="socialLink.key"
                                                    :aria-label="socialLink.label"
                                                    :href="socialLink.href"
                                                    :title="socialLink.label"
                                                    class="relationships-social-link"
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    <svg-vue :icon="socialLink.icon" class="h-4 w-4"></svg-vue>
                                                </a>
                                            </span>
                                        </td>
                                    </tr>
                                    <tr v-if="addressText(selectedRelationship)">
                                        <th scope="row">Adresse</th>
                                        <td>
                                            <a
                                                :href="addressHref(selectedRelationship)"
                                                class="relationships-contact-link"
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                {{ addressText(selectedRelationship) }}
                                            </a>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                        <p v-else class="relationships-contact-empty">Keine Kontaktdaten hinterlegt.</p>

                        <template v-if="selectedRelationship.associatedCompanies.length > 0">
                            <div class="relationships-main-section-header relationships-company-section-header">
                                <h3 class="relationships-main-section-title">Unternehmen</h3>
                            </div>

                            <div class="relationships-company-list relationships-company-section">
                                <details
                                    v-for="company in selectedRelationship.associatedCompanies"
                                    :key="company.id"
                                    class="relationships-company"
                                >
                                    <summary class="relationships-company-summary">
                                        <span class="relationships-company-identity">
                                            <img
                                                :alt="company.name"
                                                :src="company.image"
                                                class="relationships-company-avatar"
                                            >
                                            <span class="relationships-table-primary">{{ company.name }}</span>
                                        </span>
                                        <svg-vue
                                            class="relationships-company-chevron h-4 w-4 text-gray-400"
                                            icon="font-awesome.regular.chevron-right"
                                        ></svg-vue>
                                    </summary>
                                    <table class="relationships-contact-table relationships-company-detail-table">
                                        <tbody>
                                            <tr v-if="isAdmin">
                                                <th scope="row">Rolle</th>
                                                <td>{{ companyRoleLabel(company) }}</td>
                                            </tr>
                                            <tr v-if="company.vatId">
                                                <th scope="row">USt-ID</th>
                                                <td>{{ company.vatId }}</td>
                                            </tr>
                                            <tr v-if="company.email">
                                                <th scope="row">E-Mail</th>
                                                <td>
                                                    <a
                                                        :href="`mailto:${company.email}`"
                                                        class="relationships-contact-link"
                                                    >
                                                        {{ company.email }}
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="company.phone">
                                                <th scope="row">Telefon</th>
                                                <td>
                                                    <a
                                                        :href="phoneHref(company.phone)"
                                                        class="relationships-contact-link"
                                                    >
                                                        {{ company.phone }}
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="company.website">
                                                <th scope="row">Website</th>
                                                <td>
                                                    <a
                                                        :href="websiteHref(company.website)"
                                                        class="relationships-contact-link"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                    >
                                                        {{ company.website }}
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="companySocialLinks(company).length > 0" class="relationships-social-row">
                                                <th scope="row">Social Media</th>
                                                <td>
                                                    <span class="relationships-social-links">
                                                        <a
                                                            v-for="socialLink in companySocialLinks(company)"
                                                            :key="`${company.id}-${socialLink.key}`"
                                                            :aria-label="socialLink.label"
                                                            :href="socialLink.href"
                                                            :title="socialLink.label"
                                                            class="relationships-social-link"
                                                            rel="noopener noreferrer"
                                                            target="_blank"
                                                        >
                                                            <svg-vue :icon="socialLink.icon" class="h-4 w-4"></svg-vue>
                                                        </a>
                                                    </span>
                                                </td>
                                            </tr>
                                            <tr v-if="companyAddressText(company)">
                                                <th scope="row">Adresse</th>
                                                <td>
                                                    <a
                                                        :href="companyAddressHref(company)"
                                                        class="relationships-contact-link"
                                                        rel="noopener noreferrer"
                                                        target="_blank"
                                                    >
                                                        {{ companyAddressText(company) }}
                                                    </a>
                                                </td>
                                            </tr>
                                            <tr v-if="!hasVisibleCompanyDetails(company)">
                                                <td class="relationships-company-empty" colspan="2">
                                                    Keine weiteren Unternehmensdaten hinterlegt.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </details>
                            </div>
                        </template>

                        <template v-if="hasConnections">
                            <div class="relationships-main-section-header relationships-summary-section-header">
                                <h3 class="relationships-main-section-title">Verbindungen</h3>
                            </div>

                            <div
                                :class="`relationships-summary-columns-${relationshipSummary.length}`"
                                class="relationships-summary"
                            >
                                <button
                                    v-for="summary in relationshipSummary"
                                    :key="summary.key"
                                    :class="{'relationships-summary-item-clickable': connectionSummaryTarget(summary)}"
                                    :disabled="!connectionSummaryTarget(summary)"
                                    class="relationships-summary-item"
                                    type="button"
                                    @click="navigateToConnectionSummary(summary)"
                                >
                                    <div class="relationships-summary-value">
                                        <svg-vue :icon="summary.icon" class="relationships-summary-icon text-gray-400"></svg-vue>
                                        <span class="text-2xl font-semibold text-gray-800">{{ summary.value }}</span>
                                    </div>
                                    <div class="relationships-summary-label mt-1">{{ summary.label }}</div>
                                </button>
                            </div>
                        </template>

                        <template v-if="hasConnectionDetails">
                            <div class="relationships-detail-section">
                                <div class="relationships-detail-section-title">
                                    <h3 class="relationships-main-section-title">Neuste Verbindungen</h3>
                                </div>

                                <div class="relationships-intersections">
                                    <button
                                        v-for="intersection in selectedRelationship.intersections"
                                        :key="intersection.id"
                                        :class="{'relationships-intersection-row-clickable': connectionDetailTarget(intersection)}"
                                        :disabled="!connectionDetailTarget(intersection)"
                                        class="relationships-intersection-row"
                                        type="button"
                                        @click="navigateToConnectionDetail(intersection)"
                                    >
                                        <span class="relationships-intersection-icon">
                                            <svg-vue :icon="intersectionIcon(intersection.typeKey)" class="relationships-detail-icon text-gray-400"></svg-vue>
                                        </span>
                                        <div class="min-w-0 flex-1">
                                            <div class="relationships-table-primary truncate">{{ intersection.title }}</div>
                                            <div class="relationships-table-secondary mt-1 truncate">{{ intersection.description }}</div>
                                        </div>
                                        <identifier-badge
                                            v-if="intersection.typeKey === 'ticket' || intersection.typeKey === 'position'"
                                            :value="intersection.entityId"
                                            :title="`${intersection.type}-ID`"
                                            class="flex-shrink-0"
                                        ></identifier-badge>
                                    </button>
                                </div>
                            </div>
                        </template>
                        </div>
                        <footer class="relationships-detail-footer">
                            <span v-if="selectedRelationship.updatedAt" class="relationships-detail-updated">
                                <img
                                    v-if="relationshipUpdateActorAvatar(selectedRelationship)"
                                    :alt="relationshipUpdateActorName(selectedRelationship) || 'Bearbeiter'"
                                    :src="relationshipUpdateActorAvatar(selectedRelationship)"
                                    class="relationships-detail-updated-avatar"
                                >
                                <span v-if="relationshipUpdateActorName(selectedRelationship)">
                                    {{ relationshipUpdateActorName(selectedRelationship) }}
                                </span>
                                <span>{{ relationshipUpdateActorName(selectedRelationship) ? 'am ' : 'Zuletzt geändert am ' }}{{ formatUpdatedAt(selectedRelationship.updatedAt) }}</span>
                            </span>
                            <button
                                v-if="isAdmin"
                                class="relationships-detail-edit"
                                title="Person bearbeiten"
                                aria-label="Person bearbeiten"
                                type="button"
                                @click="editRelationship(selectedRelationship)"
                            >
                                <svg-vue class="h-4 w-4" icon="font-awesome.regular.pen-to-square"></svg-vue>
                            </button>
                        </footer>
                    </section>

                    <section v-else-if="relationships.length > 0" class="relationships-detail relationships-detail-empty">
                        <div class="relationships-detail-toolbar relationships-detail-toolbar-empty">
                            <span class="relationships-detail-toolbar-title">Kontaktdetails</span>
                        </div>
                        <div class="relationships-detail-empty-body">
                            <span class="stage-empty-state-icon-wrap">
                                <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                            </span>
                            <h2 class="stage-empty-state-title">Beziehung auswählen</h2>
                        </div>
                    </section>
                </div>

                <aside
                    :aria-hidden="!filtersSidebar"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-20"
                    :class="filtersSidebar ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">{{ $t('Filter & Sorting') }}</div>
                        </div>

                        <div class="stage-filter-label">{{ $t('Sort by') }}</div>
                        <div class="stage-filter-row mb-5">
                            <button
                                class="stage-filter-sort-btn text-sm leading-5 font-medium"
                                type="button"
                                @click="changeSortOrder"
                            >
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"></svg-vue>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select v-model="sort.column" class="stage-filter-sort-select block form-select text-sm leading-5 font-medium" @change="applyFilters">
                                    <option value="last_activity">Letzte Aktivität</option>
                                    <option value="connections">Verbindungen</option>
                                    <option value="name">Name</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>

                        <div class="stage-filter-label">{{ $t('Search') }}</div>
                        <div class="mb-5">
                            <div class="relative">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                </div>
                                <input
                                    v-model.lazy="filters.search"
                                    class="stage-filter-input form-input block pl-10 text-sm"
                                    placeholder="Name, Rolle oder Unternehmen"
                                    type="search"
                                    @change="applyFilters"
                                >
                            </div>
                        </div>

                        <div class="stage-filter-label">Unternehmen</div>
                        <div class="mb-5">
                            <input-select
                                id="relationships-company-filter"
                                v-model="companyFilterValue"
                                :options="companyFilterOptions"
                                class="stage-filter-select"
                                option-label="name"
                                @change="applyFilters"
                            >
                                <template v-slot:selectedOption="props">
                                    <template v-if="!props || !props.anySelected || !props.option || Number(props.option.id) === 0">
                                        <span class="block truncate">Alle Unternehmen</span>
                                    </template>
                                    <template v-else>
                                        <div class="flex min-w-0 items-center space-x-3">
                                            <img
                                                :alt="props.option.name || 'Unternehmen'"
                                                :src="resolveCompanyAvatar(props.option)"
                                                class="h-5 w-5 flex-shrink-0 rounded-sm object-cover"
                                            >
                                            <span class="block truncate">{{ props.option.name }}</span>
                                        </div>
                                    </template>
                                </template>
                                <template v-slot:selectOption="props">
                                    <div class="flex min-w-0 items-center space-x-3">
                                        <img
                                            v-if="props.option && Number(props.option.id) > 0"
                                            :alt="props.option.name || 'Unternehmen'"
                                            :src="resolveCompanyAvatar(props.option)"
                                            class="h-5 w-5 flex-shrink-0 rounded-sm object-cover"
                                        >
                                        <span
                                            :class="props.option && Number(props.option.id) === Number(companyFilterValue) ? 'font-semibold' : 'font-normal'"
                                            class="block truncate"
                                        >
                                            {{ props.option ? props.option.name : '' }}
                                        </span>
                                    </div>
                                </template>
                            </input-select>
                        </div>

                        <template v-if="canViewExternalContacts">
                            <div class="stage-filter-label">Personentyp</div>
                            <div class="mb-5">
                                <select v-model="filters.contactType" class="stage-filter-input block form-select text-sm leading-5 font-medium" @change="applyFilters">
                                    <option value="">Alle Personen</option>
                                    <option value="system_accounts">Systemaccounts</option>
                                    <option value="external_contacts">Externe Kontakte</option>
                                </select>
                            </div>
                        </template>

                        <div class="stage-filter-label">Aktivität</div>
                        <div class="mb-5">
                            <select v-model="filters.activity" class="stage-filter-input block form-select text-sm leading-5 font-medium" @change="applyFilters">
                                <option value="">Gesamter Zeitraum</option>
                                <option value="30">Letzte 30 Tage</option>
                                <option value="90">Letzte 90 Tage</option>
                                <option value="365">Letzte 12 Monate</option>
                            </select>
                        </div>

                        <button
                            v-if="hasActiveFilters"
                            class="text-sm text-gray-500 hover:text-gray-600 focus:outline-none"
                            type="button"
                            @click="resetFilters"
                        >
                            {{ $t('Reset selection') }}
                        </button>
                    </div>
                </aside>
            </div>
        </div>


        <confirm-dialog
            :open="deleteDialog.open"
            title="Externen Kontakt löschen"
            :text="deleteDialog.contact ? `${deleteDialog.contact.name} wird dauerhaft gelöscht.` : ''"
            :confirm-label="$t('Delete')"
            :close-label="$t('Cancel')"
            confirm-button-class="btn btn-red"
            :hide-close-button="true"
            :show-header-close-button="true"
            :sectioned="true"
            headline-id="external-contact-delete-dialog-headline"
            @close="closeExternalContactDeleteDialog"
            @confirm="confirmExternalContactDelete"
        />
    </div>
</template>

<script>
import RelationshipsHubToolbar from '@/components/relationships/hub-toolbar';
import ConfirmDialog from '@/components/modals/confirm-dialog';
import {resolveStoreAccess} from '@/utils/access-context';
import {routeWithReturnTo} from '@/utils/list-route-state';

const socialNetworkDefinitions = [
    {key: 'linkedin', field: 'linkedinUrl', label: 'LinkedIn', icon: 'font-awesome.brands.linkedin'},
    {key: 'xing', field: 'xingUrl', label: 'XING', icon: 'font-awesome.brands.xing'},
    {key: 'instagram', field: 'instagramUrl', label: 'Instagram', icon: 'font-awesome.brands.instagram'},
    {key: 'facebook', field: 'facebookUrl', label: 'Facebook', icon: 'font-awesome.brands.facebook'},
];

function storedRelationshipsViewMode() {
    try {
        return window.localStorage.getItem('relationships.contacts.view_mode') === 'grid' ? 'grid' : 'list';
    } catch (error) {
        return 'list';
    }
}

export default {
    name: 'workspace-relationships-index',
    components: {
        ConfirmDialog,
        RelationshipsHubToolbar,
    },
    data() {
        return {
            relationships: [],
            selectedRelationshipId: null,
            mobileDetailOpen: false,
            loading: false,
            filtersSidebar: false,
            viewMode: storedRelationshipsViewMode(),
            perPage: 20,
            pagination: {
                currentPage: 1,
                perPage: 20,
                total: 0,
                totalPages: 1,
            },
            meta: {
                can_view_external_contacts: false,
                can_create_external_contacts: false,
                companies: [],
            },
            filters: {
                search: '',
                company: '',
                contactType: '',
                activity: '',
            },
            sort: {
                column: 'last_activity',
                direction: 'desc',
            },
            deleteDialog: {
                open: false,
                contact: null,
            },
            activeActionsRelationshipId: null,
        };
    },
    computed: {
        isAdmin() {
            return !!this.accessContext.is_admin;
        },
        canCreateExternalContacts() {
            return !!this.meta.can_create_external_contacts;
        },
        canViewExternalContacts() {
            return !!this.meta.can_view_external_contacts;
        },
        companyOptions() {
            return Array.isArray(this.meta.companies) ? this.meta.companies : [];
        },
        companyFilterValue: {
            get() {
                return this.filters.company ? Number(this.filters.company) : 0;
            },
            set(value) {
                const companyId = Number(value || 0);
                this.filters.company = companyId > 0 ? String(companyId) : '';
            },
        },
        companyFilterOptions() {
            const options = this.companyOptions.map((company) => ({
                ...company,
                id: Number(company.id),
            }));

            return [{id: 0, name: 'Alle Unternehmen', image: null}, ...options];
        },
        hasActiveFilters() {
            return this.filters.search.trim() !== ''
                || this.filters.company !== ''
                || this.filters.contactType !== ''
                || this.filters.activity !== '';
        },
        paginatedRelationships() {
            return this.relationships;
        },
        filteredRelationships() {
            return this.relationships;
        },
        currentPage() {
            return this.pagination.currentPage;
        },
        totalPages() {
            return this.pagination.totalPages;
        },
        paginationStart() {
            if (this.pagination.total === 0) {
                return 0;
            }

            return ((this.pagination.currentPage - 1) * this.pagination.perPage) + 1;
        },
        paginationEnd() {
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
        selectedRelationship() {
            return this.relationships.find((relationship) => relationship.id === this.selectedRelationshipId) || null;
        },
        selectedRelationshipSocialLinks() {
            if (!this.selectedRelationship) {
                return [];
            }

            return socialNetworkDefinitions.reduce((links, network) => {
                const href = this.socialHref(this.selectedRelationship[network.field]);
                if (href) {
                    links.push({
                        key: network.key,
                        label: network.label,
                        icon: network.icon,
                        href,
                    });
                }

                return links;
            }, []);
        },
        hasSelectedRelationshipContactData() {
            if (!this.selectedRelationship) {
                return false;
            }

            return !!(
                this.selectedRelationship.email
                || this.selectedRelationship.phone
                || this.selectedRelationship.mobile
                || this.selectedRelationship.website
                || this.selectedRelationshipSocialLinks.length > 0
                || this.addressText(this.selectedRelationship)
            );
        },
        hasConnections() {
            return !!this.selectedRelationship
                && this.selectedRelationship.hasAccount
                && this.relationshipSummary.length > 0;
        },
        hasConnectionDetails() {
            return !!this.selectedRelationship
                && this.selectedRelationship.hasAccount
                && this.selectedRelationship.intersections.length > 0;
        },
        relationshipSummary() {
            if (!this.selectedRelationship) {
                return [];
            }

            const modules = this.accessContext && this.accessContext.modules
                ? this.accessContext.modules
                : {};

            return [
                {
                    key: 'companies',
                    label: 'Unternehmen',
                    value: this.selectedRelationship.companies,
                    icon: 'font-awesome.regular.buildings',
                },
                {
                    key: 'projects',
                    label: 'Projekte',
                    value: this.selectedRelationship.projects,
                    icon: 'font-awesome.regular.layer-group',
                },
                {
                    key: 'tickets',
                    label: 'Tickets',
                    value: this.selectedRelationship.tickets,
                    icon: 'font-awesome.regular.message-text',
                },
                {
                    key: 'positions',
                    label: 'Positionen',
                    value: this.selectedRelationship.positions,
                    icon: 'font-awesome.regular.diagram-next',
                },
            ].filter((summary) => {
                if (summary.key === 'positions' && !modules.positions) {
                    return false;
                }

                return Number(summary.value) > 0;
            });
        },
        accessContext() {
            return resolveStoreAccess(this.$store);
        },
        isWorkspaceRoute() {
            return String(this.$route && this.$route.path ? this.$route.path : '').startsWith('/workspace');
        },
    },
    mounted() {
        this.getRelationships();
        document.addEventListener('click', this.handleDocumentClick);
    },
    beforeUnmount() {
        document.removeEventListener('click', this.handleDocumentClick);
    },
    methods: {
        setViewMode(mode) {
            this.viewMode = mode === 'grid' ? 'grid' : 'list';
            try {
                window.localStorage.setItem('relationships.contacts.view_mode', this.viewMode);
            } catch (error) {
                // The selected view still remains active for the current session.
            }
        },
        handleDocumentClick() {
            this.closeRowActions();
        },
        toggleRowActions(relationshipId) {
            this.activeActionsRelationshipId = this.activeActionsRelationshipId === relationshipId
                ? null
                : relationshipId;
        },
        closeRowActions() {
            this.activeActionsRelationshipId = null;
        },
        relationshipBadgeLabel(relationship) {
            if (!relationship) {
                return null;
            }

            if (!relationship.hasAccount) {
                return 'Externer Kontakt';
            }

            return relationship.systemRole || null;
        },
        normalizeRelationship(item) {
            const connections = item && item.connections ? item.connections : {};
            const associatedCompanies = Array.isArray(item.associated_companies)
                ? item.associated_companies.map((company) => ({
                    id: Number(company.id) || 0,
                    name: company.name || '–',
                    image: company.image || '/images/default/icon.png',
                    website: company.website || null,
                    email: company.email || null,
                    phone: company.phone || null,
                    vatId: company.vat_id || null,
                    linkedinUrl: company.linkedin_url || null,
                    xingUrl: company.xing_url || null,
                    instagramUrl: company.instagram_url || null,
                    facebookUrl: company.facebook_url || null,
                    street: company.street || null,
                    houseNumber: company.house_number || null,
                    postalCode: company.postal_code || null,
                    city: company.city || null,
                    country: company.country || null,
                    isCustomerCompany: !!company.is_customer_company,
                    isProviderCompany: !!company.is_provider_company,
                    isContactCompany: !!company.is_contact_company,
                    isOperatorCompany: !!company.is_operator_company,
                }))
                : [];
            const primaryCompanyName = typeof item.company === 'string'
                ? item.company.trim()
                : '';
            const associatedCompany = associatedCompanies.find((company) => company.name && company.name !== '–');

            return {
                id: item.id,
                entityId: Number(item.entity_id) || 0,
                name: item.name || '–',
                salutation: item.salutation || null,
                initials: item.initials || '',
                avatar: item.avatar || null,
                avatarHue: Number(item.avatar_hue) || 210,
                hasAccount: !!item.has_account,
                systemRole: item.system_role || null,
                companyId: Number(item.company_id) || null,
                company: primaryCompanyName || (associatedCompany ? associatedCompany.name : null),
                associatedCompanies,
                role: item.role_title || null,
                email: item.email || null,
                phone: item.phone_landline || null,
                mobile: item.phone_mobile || null,
                website: item.website || null,
                linkedinUrl: item.linkedin_url || null,
                xingUrl: item.xing_url || null,
                instagramUrl: item.instagram_url || null,
                facebookUrl: item.facebook_url || null,
                street: item.street || null,
                houseNumber: item.house_number || null,
                postalCode: item.postal_code || null,
                city: item.city || null,
                country: item.country || null,
                relationshipsContactVisibleToCustomers: !!item.relationships_contact_visible_to_customers,
                relationshipsContactVisibleToEmployees: !!item.relationships_contact_visible_to_employees,
                relationshipsContactVisibleToServiceProviders: !!item.relationships_contact_visible_to_service_providers,
                customerCompanyIds: Array.isArray(item.customer_company_ids)
                    ? item.customer_company_ids.map((id) => Number(id))
                    : [],
                contactCompanyIds: Array.isArray(item.contact_company_ids)
                    ? item.contact_company_ids.map((id) => Number(id))
                    : [],
                companies: Number(connections.companies) || 0,
                projects: Number(connections.projects) || 0,
                tickets: Number(connections.tickets) || 0,
                positions: Number(connections.positions) || 0,
                updatedAt: item.updated_at || (item.last_activity ? item.last_activity.at : null),
                updatedBy: item.updated_by || null,
                lastActivity: item.last_activity && item.last_activity.title
                    ? item.last_activity.title
                    : 'Keine Aktivität',
                lastActivityAt: item.last_activity ? item.last_activity.at : null,
                intersections: Array.isArray(item.intersections)
                    ? item.intersections.map((intersection) => ({
                        id: intersection.id,
                        entityId: Number(intersection.entity_id) || 0,
                        typeKey: intersection.type,
                        type: this.intersectionLabel(intersection.type),
                        title: intersection.title || '–',
                        description: [
                            intersection.description || null,
                            intersection.updated_at ? `aktualisiert ${this.formatRelativeDate(intersection.updated_at)}` : null,
                        ].filter(Boolean).join(' · ') || '–',
                        updatedAt: intersection.updated_at || null,
                    }))
                    : [],
            };
        },
        getRelationships(preferredRelationshipId = null) {
            this.loading = true;

            return axios.get('api/workspace/relationships', {
                params: {
                    page: this.pagination.currentPage,
                    perPage: this.perPage,
                    search: this.filters.search || undefined,
                    company_id: this.filters.company || undefined,
                    contact_type: this.filters.contactType || undefined,
                    activity_days: this.filters.activity || undefined,
                    sort_column: this.sort.column,
                    sort_order: this.sort.direction,
                },
            }).then((response) => {
                const data = response && response.data ? response.data : {};
                this.relationships = Array.isArray(data.items)
                    ? data.items.map((item) => this.normalizeRelationship(item))
                    : [];
                this.pagination = {
                    currentPage: Number(data.pagination && data.pagination.currentPage) || 1,
                    perPage: Number(data.pagination && data.pagination.perPage) || this.perPage,
                    total: Number(data.pagination && data.pagination.total) || 0,
                    totalPages: Math.max(1, Number(data.pagination && data.pagination.totalPages) || 1),
                };
                this.meta = {
                    can_view_external_contacts: !!(data.meta && data.meta.can_view_external_contacts),
                    can_create_external_contacts: !!(data.meta && data.meta.can_create_external_contacts),
                    companies: data.meta && Array.isArray(data.meta.companies) ? data.meta.companies : [],
                };

                const desiredId = preferredRelationshipId || this.selectedRelationshipId;
                this.selectedRelationshipId = this.relationships.some((relationship) => relationship.id === desiredId)
                    ? desiredId
                    : (this.relationships[0] ? this.relationships[0].id : null);
            }).catch(() => {
                // The global request handler presents API errors consistently.
            }).finally(() => {
                this.loading = false;
            });
        },
        applyFilters() {
            this.pagination.currentPage = 1;
            this.getRelationships();
        },
        avatarStyle(relationship) {
            return {
                background: `linear-gradient(145deg, hsl(${relationship.avatarHue} 40% 52%), hsl(${relationship.avatarHue} 44% 36%))`,
            };
        },
        totalConnections(relationship) {
            return relationship.companies + relationship.projects + relationship.tickets + relationship.positions;
        },
        relationshipSubtitle(relationship) {
            return [
                relationship && relationship.role ? `Rolle: ${relationship.role}` : null,
                relationship ? relationship.company : null,
            ].filter(Boolean).join(' · ') || '–';
        },
        relationshipCardPhone(relationship) {
            return relationship ? (relationship.phone || relationship.mobile || '') : '';
        },
        relationshipSocialLinks(relationship) {
            if (!relationship) {
                return [];
            }

            return socialNetworkDefinitions.reduce((links, network) => {
                const href = this.socialHref(relationship[network.field]);
                if (href) {
                    links.push({
                        key: network.key,
                        label: network.label,
                        icon: network.icon,
                        href,
                    });
                }

                return links;
            }, []);
        },
        selectRelationship(relationshipId) {
            this.closeRowActions();
            this.selectedRelationshipId = relationshipId;
            this.mobileDetailOpen = true;
        },
        closeMobileDetail() {
            this.mobileDetailOpen = false;
        },
        phoneHref(phone) {
            return `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;
        },
        websiteHref(website) {
            const value = String(website || '');
            return /^https?:\/\//i.test(value) ? value : `https://${value}`;
        },
        socialHref(value) {
            const normalizedValue = String(value || '').trim();
            if (normalizedValue === '') {
                return null;
            }

            const candidate = /^https?:\/\//i.test(normalizedValue)
                ? normalizedValue
                : `https://${normalizedValue}`;

            try {
                const url = new URL(candidate);
                return ['http:', 'https:'].includes(url.protocol) ? url.href : null;
            } catch (error) {
                return null;
            }
        },
        companySocialLinks(company) {
            if (!company) {
                return [];
            }

            return socialNetworkDefinitions.reduce((links, network) => {
                const href = this.socialHref(company[network.field]);
                if (href) {
                    links.push({
                        key: network.key,
                        label: network.label,
                        icon: network.icon,
                        href,
                    });
                }

                return links;
            }, []);
        },
        companyRoleLabel(company) {
            if (company && company.isOperatorCompany) {
                return 'Betreiber';
            }

            const roles = [];
            if (company && company.isCustomerCompany) {
                roles.push('Kunde');
            }
            if (company && company.isProviderCompany) {
                roles.push('Dienstleister');
            }
            if (company && company.isContactCompany) {
                roles.push('Externer Kontakt');
            }

            return roles.join(' · ') || 'Unternehmen';
        },
        hasVisibleCompanyDetails(company) {
            if (!company) {
                return false;
            }

            return this.isAdmin
                || Boolean(company.vatId)
                || Boolean(company.email)
                || Boolean(company.phone)
                || Boolean(company.website)
                || this.companySocialLinks(company).length > 0
                || Boolean(this.companyAddressText(company));
        },
        addressText(relationship) {
            const streetAddress = [relationship.street, relationship.houseNumber].filter(Boolean).join(' ');
            const postalCity = [relationship.postalCode, relationship.city].filter(Boolean).join(' ');

            return [streetAddress, postalCity, relationship.country].filter(Boolean).join(', ');
        },
        addressHref(relationship) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.addressText(relationship))}`;
        },
        companyAddressText(company) {
            return this.addressText(company);
        },
        companyAddressHref(company) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(this.companyAddressText(company))}`;
        },
        formatRelativeDate(value) {
            if (!value) {
                return '–';
            }

            return moment(value).locale(window.app.app_date_locale || 'de').fromNow();
        },
        formatUpdatedAt(value) {
            if (!value) {
                return '–';
            }

            return this.$momentFormatDateTime(value).replace(/\s+(\d{1,2}:\d{2})$/, ', $1');
        },
        relationshipUpdateActorName(relationship) {
            return relationship && relationship.updatedBy && relationship.updatedBy.name
                ? relationship.updatedBy.name
                : null;
        },
        relationshipUpdateActorAvatar(relationship) {
            const actor = relationship && relationship.updatedBy ? relationship.updatedBy : null;
            if (!actor) {
                return null;
            }
            if (actor.avatar && actor.avatar !== 'gravatar') {
                return actor.avatar;
            }

            return actor.gravatar || null;
        },
        intersectionLabel(type) {
            return {
                company: 'Unternehmen',
                project: 'Projekt',
                ticket: 'Ticket',
                position: 'Position',
            }[type] || 'Verbindung';
        },
        intersectionIcon(type) {
            return {
                company: 'font-awesome.regular.buildings',
                project: 'font-awesome.regular.layer-group',
                ticket: 'font-awesome.regular.message-text',
                position: 'font-awesome.regular.diagram-next',
            }[type] || 'font-awesome.regular.people-arrows';
        },
        connectionSummaryTarget(summary) {
            if (!summary || Number(summary.value) < 1) {
                return null;
            }

            const access = this.accessContext;
            const modules = access && access.modules ? access.modules : {};

            if (summary.key === 'positions') {
                if (!modules.positions) {
                    return null;
                }

                const path = this.isWorkspaceRoute ? '/workspace/positions' : '/positions/list';
                const query = {
                    list_relationship_user_id: this.selectedRelationship.entityId,
                };
                if (this.isWorkspaceRoute && access.is_employee_like && !access.is_admin) {
                    query.list_entries = 'all';
                }

                return {
                    path,
                    query,
                };
            }

            if (summary.key === 'tickets') {
                if (!modules.tickets) {
                    return null;
                }

                const path = this.isWorkspaceRoute ? '/workspace/tickets' : '/tickets/list';
                return {
                    path,
                    query: {
                        list_relationship_user_id: this.selectedRelationship.entityId,
                        list_statuses: [1, 2],
                    },
                };
            }

            if (summary.key === 'projects' && modules.projects) {
                return {
                    path: this.isWorkspaceRoute ? '/workspace/projects' : '/projects/list',
                    query: {
                        list_relationship_user_id: this.selectedRelationship.entityId,
                    },
                };
            }

            const paths = {
                companies: modules.relationships
                    ? (this.isWorkspaceRoute ? '/workspace/relationships/companies' : '/relationships/companies')
                    : null,
            };

            return paths[summary.key] || null;
        },
        connectionDetailTarget(intersection) {
            if (!intersection || Number(intersection.entityId) < 1) {
                return null;
            }

            const access = this.accessContext;
            const modules = access && access.modules ? access.modules : {};
            const entityId = Number(intersection.entityId);

            if (intersection.typeKey === 'company') {
                if (access.is_admin && modules.companies) {
                    return `/workspace/relationships/companies/${entityId}/edit`;
                }

                return modules.relationships
                    ? {
                        path: this.isWorkspaceRoute ? '/workspace/relationships/companies' : '/relationships/companies',
                        query: {
                            list_search: intersection.title,
                        },
                    }
                    : null;
            }

            if (intersection.typeKey === 'project') {
                if (!modules.projects) {
                    return null;
                }

                if (access.is_admin && this.isWorkspaceRoute) {
                    return `/workspace/projects/${entityId}/edit`;
                }

                return {
                    path: this.isWorkspaceRoute ? '/workspace/projects' : '/projects/list',
                    query: {
                        list_search: intersection.title,
                    },
                };
            }

            if (intersection.typeKey === 'ticket' && modules.tickets) {
                return this.isWorkspaceRoute
                    ? `/workspace/tickets/${entityId}/manage`
                    : `/tickets/${entityId}`;
            }

            if (intersection.typeKey === 'position' && modules.positions) {
                return this.isWorkspaceRoute
                    ? `/workspace/positions/${entityId}/manage`
                    : `/positions/${entityId}`;
            }

            return null;
        },
        navigateToConnectionSummary(summary) {
            const target = this.connectionSummaryTarget(summary);
            if (target) {
                this.$router.push(this.connectionTargetWithReturnTo(target));
            }
        },
        navigateToConnectionDetail(intersection) {
            const target = this.connectionDetailTarget(intersection);
            if (target) {
                this.$router.push(this.connectionTargetWithReturnTo(target));
            }
        },
        connectionTargetWithReturnTo(target) {
            if (typeof target === 'string') {
                return routeWithReturnTo(target, this.$route, this.$route.path);
            }

            return {
                ...target,
                query: {
                    ...(target.query || {}),
                    return_to: this.$route && this.$route.fullPath ? this.$route.fullPath : this.$route.path,
                },
            };
        },
        toggleFiltersSidebar() {
            this.filtersSidebar = !this.filtersSidebar;
        },
        changeSortOrder() {
            this.sort.direction = this.sort.direction === 'asc' ? 'desc' : 'asc';
            this.applyFilters();
        },
        changePage(page) {
            const nextPage = Math.min(Math.max(Number(page) || 1, 1), this.pagination.totalPages);
            if (nextPage === this.pagination.currentPage) {
                return;
            }

            this.pagination.currentPage = nextPage;
            this.getRelationships();
        },
        resetFilters() {
            this.filters = {
                search: '',
                company: '',
                contactType: '',
                activity: '',
            };
            this.pagination.currentPage = 1;
            this.getRelationships();
        },
        openExternalContactDialog() {
            if (!this.canCreateExternalContacts) {
                return;
            }

            this.$router.push('/workspace/relationships/external-contacts/new');
        },
        editRelationship(relationship) {
            if (!this.isAdmin || !relationship) {
                return;
            }

            this.closeRowActions();
            if (relationship.hasAccount) {
                this.$router.push(routeWithReturnTo(
                    `/workspace/admin/users/${relationship.entityId}/edit`,
                    this.$route,
                    '/workspace/relationships'
                ));
                return;
            }

            this.$router.push(routeWithReturnTo(
                `/workspace/relationships/external-contacts/${relationship.entityId}/edit`,
                this.$route,
                '/workspace/relationships'
            ));
        },
        openExternalContactDeleteDialog(relationship) {
            if (!this.isAdmin || !relationship || relationship.hasAccount) {
                return;
            }

            this.closeRowActions();
            this.deleteDialog = {
                open: true,
                contact: relationship,
            };
        },
        closeExternalContactDeleteDialog() {
            this.deleteDialog = {
                open: false,
                contact: null,
            };
        },
        confirmExternalContactDelete() {
            const relationship = this.deleteDialog.contact;
            if (!this.isAdmin || !relationship) {
                this.closeExternalContactDeleteDialog();
                return;
            }

            axios.delete(`api/workspace/relationships/external-contacts/${relationship.entityId}`).then((response) => {
                this.closeExternalContactDeleteDialog();
                this.$notify({
                    title: this.$i18n.t('Success').toString(),
                    text: this.$i18n.t(
                        response.data && response.data.message
                            ? response.data.message
                            : 'Data deleted correctly'
                    ).toString(),
                    type: 'success',
                });
                return this.getRelationships();
            }).catch(() => {
                this.closeExternalContactDeleteDialog();
            });
        },
        resolveCompanyAvatar(company) {
            if (!company) {
                return '/images/default/icon.png';
            }

            return company.avatar_url || company.avatar || company.image || company.gravatar || '/images/default/icon.png';
        },
    },
    metaInfo() {
        return {
            title: 'Beziehungen',
        };
    },
};
</script>

<style scoped>
.relationships-layout {
    --relationships-text-strong: var(--ui-text-strong);
    --relationships-text-primary: var(--ui-text-secondary);
    --relationships-text-muted: var(--ui-text-disabled);
    --relationships-text-subtle: var(--ui-text-subtle);
    --relationships-border: var(--ui-border-default);
    display: grid;
    grid-template-columns: minmax(34rem, 3fr) minmax(25rem, 2fr);
    height: 100%;
    min-height: 0;
    overflow: hidden;
}

.relationships-layout-empty {
    grid-template-columns: minmax(0, 1fr);
}

.relationships-list {
    min-width: 0;
    border-right: 1px solid var(--border-ultralight);
}

.relationships-list-body {
    min-width: 0;
    overflow-x: hidden;
}

.relationships-list-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(100%, 27rem), 1fr));
    align-content: start;
    gap: 1rem;
    width: 100%;
    min-width: 0;
    padding: 1rem;
    overflow-x: hidden;
    background: #fff;
}

.relationships-list-grid > .relationships-row-card {
    width: 100%;
    min-width: 0;
    max-width: 100%;
}

.relationships-list-grid > .stage-empty-state {
    grid-column: 1 / -1;
}

.relationships-pagination {
    background-color: var(--ui-surface-raised) !important;
}

.relationships-table-header {
    grid-template-columns: minmax(15rem, 1fr) 7.5rem minmax(11rem, 0.8fr);
    gap: 1rem;
    padding: 0.75rem 1.5rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
    color: var(--relationships-text-primary);
    font-size: 0.7rem;
    font-weight: 600;
    letter-spacing: 0.025em;
}

.relationships-table-header-admin,
.relationships-row.relationships-row-admin,
.relationships-row.relationships-row-list-actions {
    grid-template-columns: minmax(15rem, 1fr) minmax(8rem, 0.35fr) 4.5rem;
    padding-right: 0.75rem;
}

.relationships-table-header-admin > :last-child {
    text-align: right;
}

.relationships-row {
    position: relative;
    display: grid;
    grid-template-columns: minmax(15rem, 1fr) minmax(8rem, 0.35fr) 4.5rem;
    align-items: center;
    gap: 1rem;
    width: 100%;
    min-height: 5.25rem;
    padding: 0.85rem 1.5rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: #fff;
    text-align: left;
    transition: background-color 150ms ease-out;
}

.relationships-row:nth-child(even) {
    background: var(--ui-surface-raised);
}

.relationships-row:hover,
.relationships-row:focus {
    outline: none;
}

.relationships-row-selected {
    position: relative;
}

.relationships-row-selected::before {
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    width: 4px;
    background: var(--ui-text-secondary);
    content: '';
    pointer-events: none;
}

.relationships-row.relationships-row-card,
.relationships-row.relationships-row-card.relationships-row-admin {
    display: flex;
    min-height: 10.75rem;
    align-items: stretch;
    flex-direction: column;
    gap: 0;
    padding: 1rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
    box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
}

.relationships-row-card:nth-child(even),
.relationships-row-card:last-child {
    border-bottom: 1px solid var(--border-ultralight);
    background: #fff;
}

.relationships-row-card.relationships-row-admin .relationships-person {
    padding-right: 2rem;
}

.relationships-row-card .relationships-person {
    width: 100%;
}

.relationships-row-card .relationships-person > :last-child {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
}

.relationships-row-card .relationships-person-title {
    width: 100%;
}

.relationships-row-card .relationships-actions {
    position: absolute;
    top: 1rem;
    right: 0.5rem;
    height: 2.75rem;
}

.relationships-row-card .relationships-avatar {
    width: 2.75rem;
    height: 2.75rem;
    flex-basis: 2.75rem;
}

.relationships-card-details {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.4rem;
    margin-top: 0.75rem;
    padding-top: 0.65rem;
    border-top: 1px solid var(--border-ultralight);
}

.relationships-card-line {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.5rem;
    overflow: hidden;
    color: var(--relationships-text-primary);
    font-size: 0.8125rem;
    line-height: 1.25rem;
}

.relationships-card-link {
    color: inherit;
    cursor: pointer;
    text-decoration: none;
}

.relationships-card-link:hover,
.relationships-card-link:focus {
    text-decoration: none;
}

.relationships-card-social-links {
    display: inline-flex;
    min-height: 1.25rem;
    align-items: center;
    gap: 0.55rem;
}

.relationships-card-social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--relationships-text-muted);
}

.relationships-card-social-link:hover,
.relationships-card-social-link:focus {
    color: var(--relationships-text-primary);
    outline: none;
}

.relationships-person {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    min-width: 0;
}

.relationships-person-title {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    color: var(--relationships-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.relationships-person-title > .truncate {
    min-width: 0;
}

.relationships-account-badge {
    display: inline-flex;
    align-items: center;
    min-height: 1.35rem;
    padding: 0.15rem 0.45rem;
    flex: 0 0 auto;
    border: 1px solid var(--relationships-border);
    border-radius: 0.25rem;
    background: #fff;
    color: var(--relationships-text-subtle);
    font-size: 0.65rem;
    font-weight: 600;
    line-height: 1;
}

.relationships-avatar {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    flex: 0 0 auto;
    border-radius: 9999px;
    color: rgba(255, 255, 255, 0.88);
    font-size: 0.75rem;
    font-weight: 600;
    letter-spacing: 0.03em;
}

.relationships-avatar-large {
    width: 4.5rem;
    height: 4.5rem;
    font-size: 1.125rem;
}

.relationships-count {
    text-align: left;
    color: var(--ui-text-subtle);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
    white-space: nowrap;
}

.relationships-row-arrow {
    display: none;
}

.relationships-actions {
    align-self: stretch;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 0;
    text-align: right;
}

.relationships-actions-menu {
    width: max-content;
    text-transform: none;
    letter-spacing: normal;
    white-space: nowrap;
}

.relationships-detail-backdrop {
    display: none;
}

.relationships-detail {
    display: flex;
    min-width: 0;
    height: 100%;
    max-height: 100%;
    flex-direction: column;
    overflow: hidden;
    background: #fff;
}

.relationships-detail-body {
    flex: 1;
    min-height: 0;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    padding: 1.5rem 1.75rem 2rem;
}

.relationships-detail-footer {
    display: flex;
    height: 3.5rem;
    min-height: 3.5rem;
    flex-shrink: 0;
    align-items: center;
    justify-content: space-between;
    padding: 0 1.5rem;
    background: var(--ui-surface-raised);
    box-shadow: inset 0 1px 0 var(--border-ultralight);
}

.relationships-detail-updated {
    display: inline-flex;
    min-width: 0;
    align-items: center;
    gap: 0.375rem;
    overflow: hidden;
    color: var(--ui-text-subtle);
    font-size: 0.75rem;
    line-height: 1rem;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.relationships-detail-updated-avatar {
    width: 1.25rem;
    height: 1.25rem;
    flex: 0 0 1.25rem;
    border-radius: 9999px;
    object-fit: cover;
}

.relationships-detail-toolbar {
    position: sticky;
    top: -1.5rem;
    z-index: 10;
    display: flex;
    min-height: 3.25rem;
    align-items: center;
    gap: 0.75rem;
    flex-shrink: 0;
    margin: -1.5rem -1.75rem 1.5rem;
    padding: 0 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: var(--ui-surface-raised);
}

.relationships-detail-toolbar-title {
    min-width: 0;
    color: var(--ui-text-strong);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.relationships-detail-close,
.relationships-detail-edit {
    display: inline-flex;
    width: 2rem;
    height: 2rem;
    align-items: center;
    justify-content: center;
    border: 0;
    background: transparent;
    color: var(--ui-text-subtle);
    cursor: pointer;
}

.relationships-detail-close {
    display: none;
}

.relationships-detail-edit {
    margin-left: auto;
}

.relationships-detail-close:hover,
.relationships-detail-edit:hover {
    color: var(--ui-text-secondary);
}

.relationships-detail-close:focus,
.relationships-detail-edit:focus {
    outline: none;
}

.relationships-detail-toolbar-empty {
    pointer-events: none;
}

.relationships-detail-header {
    display: flex;
    align-items: flex-start;
    gap: 1rem;
    min-height: 4.75rem;
}

.relationships-main-section-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
}

.relationships-contact-section-header,
.relationships-company-section-header,
.relationships-summary-section-header {
    margin-top: 1.5rem;
}

.relationships-main-section-title {
    color: var(--relationships-text-strong);
    font-size: 1rem;
    font-weight: 600;
    line-height: 1.5rem;
}

.relationships-contact-details {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.relationships-contact-table {
    width: 100%;
    table-layout: fixed;
    border-collapse: collapse;
}

.relationships-contact-table tr {
    border-bottom: 1px solid var(--border-ultralight);
}

.relationships-contact-table tr:nth-child(even) {
    background: var(--ui-surface-raised);
}

.relationships-contact-table tr:last-child {
    border-bottom: 0;
}

.relationships-contact-table th,
.relationships-contact-table td {
    padding: 0.65rem 1rem;
    text-align: left;
    vertical-align: middle;
}

.relationships-contact-table th {
    width: 7rem;
    color: var(--relationships-text-muted);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
}

.relationships-contact-table td {
    overflow-wrap: anywhere;
    color: var(--relationships-text-primary);
    font-size: 0.875rem;
    font-weight: 400;
    line-height: 1.25rem;
}

.relationships-contact-empty {
    margin-top: 0.75rem;
    padding: 0.75rem 1rem;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    color: var(--relationships-text-muted);
    font-size: 0.875rem;
}

.relationships-company-list {
    display: flex;
    flex-direction: column;
}

.relationships-company-section {
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.relationships-company + .relationships-company {
    border-top: 1px solid var(--border-ultralight);
}

.relationships-company-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.55rem 1rem;
    cursor: pointer;
    list-style: none;
}

.relationships-company-summary::-webkit-details-marker {
    display: none;
}

.relationships-company-identity {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 0.75rem;
}

.relationships-company-avatar {
    width: 1.75rem;
    height: 1.75rem;
    flex: 0 0 1.75rem;
    border-radius: 9999px;
    object-fit: cover;
}

.relationships-company-chevron {
    flex: 0 0 auto;
    transition: transform 150ms ease-out;
}

.relationships-company[open] .relationships-company-chevron {
    transform: rotate(90deg);
}

.relationships-company[open] .relationships-company-summary {
    border-bottom: 1px solid var(--border-ultralight);
}

.relationships-company-detail-table {
    border-top: 0;
}

.relationships-company-detail-table td.relationships-company-empty {
    color: var(--relationships-text-muted);
}

.relationships-contact-link {
    color: inherit;
    cursor: pointer;
    text-decoration: none;
}

.relationships-contact-link:hover,
.relationships-contact-link:focus {
    color: inherit;
    text-decoration: none;
}

.relationships-social-links {
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
    vertical-align: middle;
}

.relationships-social-link {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: var(--relationships-text-muted);
}

.relationships-social-link:hover,
.relationships-social-link:focus {
    color: var(--relationships-text-primary);
    outline: none;
}

.relationships-table-primary {
    color: var(--relationships-text-primary);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.relationships-table-secondary {
    color: var(--relationships-text-muted);
    font-size: 0.75rem;
    font-weight: 400;
    line-height: 1rem;
}

.relationships-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    width: 100%;
    margin-top: 0.75rem;
    overflow: hidden;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    box-shadow: 0 1px 2px rgb(0 0 0 / 5%);
}

.relationships-summary-columns-3 {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    width: 75%;
}

.relationships-summary-columns-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 50%;
}

.relationships-summary-columns-1 {
    grid-template-columns: minmax(0, 1fr);
    width: 25%;
}

.relationships-summary-item {
    min-width: 0;
    width: 100%;
    padding: 1.35rem 1.35rem;
    border-right: 1px solid var(--border-ultralight);
    background: #fff;
    text-align: left;
}

.relationships-summary-item-clickable {
    cursor: pointer;
}

.relationships-summary-item-clickable:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--relationships-text-subtle);
    outline-offset: -2px;
}

.relationships-summary-value {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
}

.relationships-summary-icon,
.relationships-detail-icon {
    width: 1.375rem;
    height: 1.375rem;
}

.relationships-summary-label {
    color: var(--relationships-text-primary);
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.relationships-summary-item:last-child {
    border-right: 0;
}

.relationships-detail-section {
    display: flex;
    flex: 0 0 auto;
    flex-direction: column;
    min-height: auto;
    margin-top: 1.5rem;
}

.relationships-detail-section-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.75rem;
}

.relationships-intersections {
    flex: 0 0 auto;
    min-height: auto;
    overflow: visible;
    border: 1px solid var(--border-ultralight);
    border-radius: 0.25rem;
    background: #fff;
}

.relationships-intersection-row {
    display: flex;
    align-items: center;
    gap: 0.85rem;
    width: 100%;
    min-height: 4.25rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border-ultralight);
    background: #fff;
    text-align: left;
}

.relationships-intersection-row:nth-child(even) {
    background: var(--ui-surface-raised);
}

.relationships-intersection-row-clickable {
    cursor: pointer;
}

.relationships-intersection-row-clickable:focus-visible {
    position: relative;
    z-index: 1;
    outline: 2px solid var(--relationships-text-subtle);
    outline-offset: -2px;
}

.relationships-intersection-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.375rem;
    height: 1.375rem;
    flex: 0 0 auto;
}

.relationships-detail-empty {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    text-align: center;
}

.relationships-detail-empty-body {
    display: flex;
    min-height: 0;
    flex: 1;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.relationships-form-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--ui-text-secondary);
    font-size: 0.8125rem;
    font-weight: 500;
    line-height: 1.25rem;
}

.relationships-form-control {
    display: block;
    width: 100%;
    border-color: var(--ui-border-default);
    border-radius: 0.25rem;
    color: var(--ui-text-secondary);
    font-size: 0.875rem;
    line-height: 1.25rem;
}

@media (max-width: 1279px) {
    .relationships-surface {
        overflow: hidden;
    }

    .relationships-layout {
        position: relative;
        display: block;
        height: 100%;
        min-height: 0;
        overflow: hidden;
    }

    .relationships-list {
        height: 100%;
        min-height: 0;
        border-right: 0;
        border-bottom: 0;
    }

    .relationships-detail-backdrop {
        position: absolute;
        inset: 0;
        z-index: 20;
        display: block;
        width: 100%;
        height: 100%;
        padding: 0;
        border: 0;
        background: transparent !important;
        box-shadow: none !important;
        opacity: 1;
        filter: none;
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        cursor: pointer;
    }

    .relationships-detail {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        z-index: 30;
        width: 92%;
        height: 100%;
        max-height: none;
        padding: 0;
        border-left: 1px solid var(--border-ultralight);
        background: #fff;
        box-shadow: none;
        visibility: hidden;
        transform: translateX(100%);
        transition: transform 180ms ease-out, visibility 0s linear 180ms;
    }

    .relationships-detail-body {
        padding: 1.25rem 1rem 1.75rem;
    }

    .relationships-detail.relationships-detail-mobile-open {
        visibility: visible;
        transform: translateX(0);
        transition-delay: 0s;
    }

    .relationships-detail-toolbar {
        top: -1.25rem;
        margin: -1.25rem -1rem 1.25rem;
    }

    .relationships-detail-close {
        display: inline-flex;
    }

    .relationships-table-header,
    .relationships-row {
        grid-template-columns: minmax(0, 1fr) 3.5rem;
    }

    .relationships-table-header-admin,
    .relationships-row-admin {
        grid-template-columns: minmax(0, 1fr) 3.5rem;
    }

    .relationships-row.relationships-row-list-actions {
        grid-template-columns: minmax(0, 1fr) 2.25rem;
        padding-right: 0.5rem;
    }

    .relationships-count {
        display: none;
    }

    .relationships-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .relationships-summary-columns-1 {
        grid-template-columns: minmax(0, 1fr);
        width: 50%;
    }

    .relationships-summary-columns-2,
    .relationships-summary-columns-3,
    .relationships-summary-columns-4 {
        width: 100%;
    }

    .relationships-summary-item:nth-child(2) {
        border-right: 0;
    }

    .relationships-summary-item:nth-child(-n+2) {
        border-bottom: 1px solid var(--border-ultralight);
    }

    .relationships-summary-item:last-child {
        border-bottom: 0;
    }

    .relationships-summary-columns-2 .relationships-summary-item {
        border-bottom: 0;
    }

    .relationships-summary-columns-3 .relationships-summary-item:last-child {
        grid-column: 1 / -1;
    }

    .relationships-list-grid {
        grid-template-columns: minmax(0, 1fr);
    }
}

@media (max-width: 1023px) {
    .relationships-row {
        grid-template-columns: minmax(0, 1fr) 1rem;
        min-height: 4.75rem;
        padding: 0.85rem 1rem;
    }

    .relationships-row-admin {
        grid-template-columns: minmax(0, 1fr) 2.25rem;
    }

    .relationships-row.relationships-row-card,
    .relationships-row.relationships-row-card.relationships-row-admin {
        grid-template-columns: minmax(0, 1fr) auto;
    }

    .relationships-activity {
        display: none;
    }

    .relationships-row-card .relationships-activity {
        display: block;
    }

    .relationships-row-arrow {
        display: block;
    }

    .relationships-row-admin .relationships-row-arrow {
        display: none;
    }

}

@media (max-width: 639px) {
    .relationships-summary {
        grid-template-columns: 1fr;
        width: 100%;
    }

    .relationships-summary-item,
    .relationships-summary-item:nth-child(2) {
        border-right: 0;
        border-bottom: 1px solid var(--border-ultralight);
    }

    .relationships-summary-item:last-child {
        border-bottom: 0;
    }

}
</style>
